import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, AlertCircle, Check, Shield, X } from 'lucide-react';

const ChangePasswordModal = ({ onClose, onSubmit, tempPassword }) => {
  const [passwords, setPasswords] = useState({
    currentPassword: tempPassword || '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [validations, setValidations] = useState({
    minLength:      false,
    hasUpperCase:   false,
    hasLowerCase:   false,
    hasNumber:      false,
    hasSpecial:     false,
    passwordsMatch: false,
    notSameAsTemp:  false,
  });

  useEffect(() => {
    const { newPassword, confirmPassword } = passwords;
    const temp = tempPassword || passwords.currentPassword;
    setValidations({
      minLength:      newPassword.length >= 8,
      hasUpperCase:   /[A-Z]/.test(newPassword),
      hasLowerCase:   /[a-z]/.test(newPassword),
      hasNumber:      /\d/.test(newPassword),
      hasSpecial:     /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      passwordsMatch: newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword,
      notSameAsTemp:  newPassword.length > 0 && newPassword !== temp,
    });
  }, [passwords, tempPassword]);

  const isFormValid = Object.values(validations).every(v => v) && passwords.currentPassword;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (passwords.newPassword === (tempPassword || passwords.currentPassword)) {
      setError('Le nouveau mot de passe ne peut pas être identique au mot de passe temporaire.');
      return;
    }
    if (!isFormValid) {
      setError('Veuillez corriger les erreurs avant de soumettre.');
      return;
    }
    setLoading(true);
   try {
      await onSubmit(passwords.currentPassword, passwords.newPassword);
    } catch (err) {
      const msg = err?.message || err?.error || err?.toString() || '';
      if (msg.includes('déjà utilisé') || msg.includes('already used')) {
        setError('Ce mot de passe a déjà été utilisé. Veuillez lire les consignes et saisir un nouveau mot de passe.');
      } else if (msg.includes('temporaire') || msg.includes('identical')) {
        setError('Le nouveau mot de passe ne peut pas être identique au mot de passe temporaire.');
      } else {
        setError(msg || 'Une erreur est survenue. Veuillez réessayer.');
      }
    }
    
    finally {
      setLoading(false);
    }
  };

  const inputFocus = (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(68,134,150,0.12)'; };
  const inputBlur  = (e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; };

  const VALIDATIONS_LIST = [
    { key: 'minLength',      label: '8 caractères minimum' },
    { key: 'hasUpperCase',   label: 'Une lettre majuscule' },
    { key: 'hasLowerCase',   label: 'Une lettre minuscule' },
    { key: 'hasNumber',      label: 'Un chiffre' },
    { key: 'hasSpecial',     label: 'Un caractère spécial (!@#$%^&*)' },
    { key: 'notSameAsTemp',  label: 'Différent du mot de passe temporaire' },
    { key: 'passwordsMatch', label: 'Les mots de passe correspondent', onlyWhenConfirm: true },
  ];

  const inputStyle = {
    width: '100%', padding: '11px 14px 11px 40px',
    border: '1.5px solid #e2e8f0', borderRadius: 12,
    background: '#f8fafc', fontSize: 14, fontWeight: 500,
    color: '#0f172a', outline: 'none', transition: 'all 0.2s',
    fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-8"
      style={{ background: 'rgba(10,15,30,0.72)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full relative"
        style={{ animation: 'fadeIn 0.25s ease-out', maxWidth: 780 }}>

    

        {/* Contenu */}
        <div style={{ padding: '28px 32px 32px' }}>

          {/* Header centré */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 14px',
              background: 'linear-gradient(135deg, #448696, #367080)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(68,134,150,0.35)' }}>
              <Shield color="white" size={26} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 4px', letterSpacing: '-0.3px' }}>
              Changement de mot de passe
            </h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0, fontWeight: 500 }}>
              Pour des raisons de sécurité, définissez un nouveau mot de passe personnel.
            </p>
          </div>

          {/* Alerte info */}
          <div style={{ padding: '11px 14px', borderRadius: 12, background: 'rgba(68,134,150,0.06)',
            border: '1px solid rgba(68,134,150,0.2)', marginBottom: 22,
            display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Shield size={15} color="#448696" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 12, color: '#448696', margin: 0, lineHeight: 1.5, fontWeight: 500 }}>
              Votre nouveau mot de passe doit être différent du mot de passe temporaire qui vous a été fourni, et différent de vos anciens mots de passe.
            </p>
          </div>

          {/* Erreur globale */}
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 12, background: '#fef2f2',
              border: '1px solid #fecaca', marginBottom: 18,
              display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0 }} />
              <p style={{ fontSize: 13, color: '#dc2626', margin: 0, fontWeight: 600 }}>{error}</p>
            </div>
          )}

          {/* Layout 2 colonnes */}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>

              {/* ── Colonne gauche — champs ── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                {/* Mot de passe temporaire */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block',
                    marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Mot de passe temporaire
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={15} color="#94a3b8" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
                    <input type={showPassword ? 'text' : 'password'} name="currentPassword"
                      value={passwords.currentPassword} onChange={handleChange}
                      disabled={!!tempPassword} style={{ ...inputStyle, background: tempPassword ? '#f1f5f9' : '#f8fafc' }}
                      onFocus={inputFocus} onBlur={inputBlur}
                      placeholder="Mot de passe temporaire reçu" required />
                  </div>
                </div>

                {/* Nouveau mot de passe */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block',
                    marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Nouveau mot de passe
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={15} color="#94a3b8" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
                    <input type={showPassword ? 'text' : 'password'} name="newPassword"
                      value={passwords.newPassword} onChange={handleChange}
                      style={{ ...inputStyle, paddingRight: 42 }}
                      onFocus={inputFocus} onBlur={inputBlur}
                      placeholder="Choisissez un mot de passe sécurisé" required />
                    <button type="button" onClick={() => setShowPassword(p => !p)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                        border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', padding: 2 }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Confirmer */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block',
                    marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Confirmer le mot de passe
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={15} color="#94a3b8" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
                    <input type={showPassword ? 'text' : 'password'} name="confirmPassword"
                      value={passwords.confirmPassword} onChange={handleChange}
                      style={inputStyle} onFocus={inputFocus} onBlur={inputBlur}
                      placeholder="Répétez le nouveau mot de passe" required />
                  </div>
                </div>

                {/* Footer sécurité */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 4 }}>
                  <Shield size={12} color="#448696" />
                  <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                    Connexion sécurisée · DANICED GROUP
                  </span>
                </div>
              </div>

              {/* ── Colonne droite — critères + bouton ── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                {/* Checklist */}
                <div style={{ padding: '18px 20px', borderRadius: 14,
                  background: '#f8fafc', border: '1.5px solid #f0f0f0', flex: 1 }}>
                  <p style={{ fontSize: 11, fontWeight: 800, color: '#475569', margin: '0 0 14px',
                    textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Critères de sécurité
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {VALIDATIONS_LIST.map(v => {
                      if (v.onlyWhenConfirm && !passwords.confirmPassword) return null;
                      const ok = validations[v.key];
                      return (
                        <div key={v.key} style={{ display: 'flex', alignItems: 'center', gap: 10,
                          transition: 'all 0.2s' }}>
                          <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                            background: ok ? '#059669' : '#e2e8f0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'background 0.2s', boxShadow: ok ? '0 2px 6px rgba(5,150,105,0.3)' : 'none' }}>
                            {ok
                              ? <Check size={11} color="white" strokeWidth={3} />
                              : <X size={10} color="#94a3b8" strokeWidth={2.5} />}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: ok ? 600 : 500,
                            color: ok ? '#059669' : '#94a3b8', transition: 'color 0.2s' }}>
                            {v.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Bouton submit */}
                <button type="submit" disabled={loading || !isFormValid}
                  style={{ width: '100%', padding: '14px 24px', borderRadius: 12,
                    border: 'none', fontWeight: 700, fontSize: 14, cursor: isFormValid && !loading ? 'pointer' : 'not-allowed',
                    background: isFormValid ? 'linear-gradient(135deg, #448696, #367080)' : '#e2e8f0',
                    color: isFormValid ? 'white' : '#94a3b8',
                    boxShadow: isFormValid ? '0 4px 14px rgba(68,134,150,0.35)' : 'none',
                    transition: 'all 0.2s', opacity: loading ? 0.7 : 1,
                    fontFamily: 'DM Sans, sans-serif' }}
                  onMouseEnter={(e) => { if (isFormValid && !loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Mise à jour en cours...
                    </span>
                  ) : 'Définir mon nouveau mot de passe'}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

export default ChangePasswordModal;